/**
 * ChatImageGrid — WhatsApp-style image thumbnail layout for chat messages (web).
 *
 * Layout rules (TA-9682):
 *  - Single image: width = maxWidth, height from clamped ratio [0.75, 2.0]
 *  - 2 images: first landscape → vertical stack; first portrait/square → side by side
 *  - 3 images: first landscape → top full-width + 2 half-width;
 *              first portrait/square → hero-left + 2 stacked right
 *  - 4+ images: 2x2 grid, 5+ shows +N overlay on 4th cell (only first 4 render)
 *  - resizeMode=cover, gap=4px, borderRadius=12px
 *  - maxWidth is supplied by the caller — it is the bubble width budget for
 *    this message row (sent vs received already encoded by the parent).
 *
 * Mobile is out of scope (TA-9682). Use ChatImages for non-chat-bubble call sites.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Image as RNImage,
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';

import CachedImage from '@components/CachedImage/CachedImage';
import PlayCircle from '@components/Icons/PlayCircle';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import { Document, LocalFile } from '@graphql/generated';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import theme from '@themes/theme';

export const CHAT_IMAGE_GRID_GAP = theme.spacing.xxs;

interface ChatImageGridProps {
  list: (Document | LocalFile)[];
  /** Bubble width budget for this message row, in px. Caller computes from chatDrawerWidth. */
  maxWidth: number;
  onPress?: (image: Document | LocalFile, index: number) => void;
  onLongPress?: () => void;
}

const isVideoItem = (item: Document | LocalFile): boolean =>
  !!(item as Document).isVideo || !!item.contentType?.includes('video');

const resolveImageUri = (
  item: Document | LocalFile | undefined
): string | null => {
  if (!item) return null;
  if (item.__typename === 'Document') {
    const doc = item as Document;
    if (isVideoItem(doc)) return doc.file?.thumbnail ?? null;
    if (doc.file?.url) return doc.file.url;
    if (doc.file?.thumbnail) return doc.file.thumbnail;
    if (doc.file?.cdnBaseUrl && doc.file?.path) {
      return `${doc.file.cdnBaseUrl}${doc.file.path}`;
    }
    return doc.file?.cdnBaseUrl ?? null;
  }
  const local = item as LocalFile;
  if (isVideoItem(local)) return local.thumbnail ?? null;
  return local.path ?? local.url ?? null;
};

const useNaturalDims = (uri: string | null) => {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    if (!uri) {
      setDims(null);
      return;
    }
    let cancelled = false;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const htmlImg = new window.Image();
      htmlImg.onload = () => {
        if (!cancelled)
          setDims({ w: htmlImg.naturalWidth, h: htmlImg.naturalHeight });
      };
      htmlImg.onerror = () => {
        if (!cancelled) setDims({ w: 1, h: 1 });
      };
      htmlImg.src = uri;
    } else {
      RNImage.getSize(
        uri,
        (w, h) => !cancelled && setDims({ w, h }),
        () => !cancelled && setDims({ w: 1, h: 1 })
      );
    }
    return () => {
      cancelled = true;
    };
  }, [uri]);

  return dims;
};

const ChatImageGrid: React.FC<ChatImageGridProps> = ({
  list,
  maxWidth,
  onPress,
  onLongPress,
}) => {
  const { height: windowH } = useWindowDimensions();
  const count = list.length;

  const maxHeight = Math.round(windowH * 0.8);

  const firstUri = useMemo(() => resolveImageUri(list[0]), [list]);
  const firstDims = useNaturalDims(firstUri);

  const { uploadingFiles, retryUpload } = useChatUploadManager();
  const uploadingStatusMap = useMemo(() => {
    if (!uploadingFiles?.length) return {};
    return Object.fromEntries(
      uploadingFiles.map((f) => [
        f.file.clientId,
        { progress: f.progress ?? 0, status: f.status ?? '' },
      ])
    );
  }, [uploadingFiles]);

  if (count === 0) return null;

  // First image ratio drives both single-image sizing and multi-image layout choice.
  // Single: clamp [0.75, 2.0]. Multi: clamp [0.5, 2.0].
  const naturalRatio = firstDims ? firstDims.w / Math.max(firstDims.h, 1) : 1;

  // Single image dimensions.
  let singleDims: { w: number; h: number } | null = null;
  if (count === 1) {
    if (!firstDims) {
      singleDims = null;
    } else {
      const clamped = Math.max(0.75, Math.min(2.0, naturalRatio));
      const w = maxWidth;
      const h = Math.min(Math.round(w / clamped), maxHeight);
      singleDims = { w, h };
    }
  }

  // Multi-image cell sizing (first ratio drives orientation + cell heights).
  const cellSize = Math.round((maxWidth - CHAT_IMAGE_GRID_GAP) / 2);
  const multiFirstRatio = firstDims
    ? Math.max(0.5, Math.min(2.0, naturalRatio))
    : 1;
  const isFirstLandscape = multiFirstRatio > 1;
  const cellH = Math.round(cellSize / multiFirstRatio);
  const fullH = Math.min(Math.round(maxWidth / multiFirstRatio), maxHeight);

  const renderCell = (
    item: Document | LocalFile,
    index: number,
    width: number,
    height: number,
    showOverlay = false
  ) => {
    const isDoc = item.__typename === 'Document';
    const image = isDoc ? (item as Document).file : (item as LocalFile);
    const clientId = item.clientId ?? item.id;
    const status = uploadingStatusMap[clientId]?.status;
    const progress = uploadingStatusMap[clientId]?.progress ?? 0;
    const blobId = (item as Document).blobId ?? '';

    const isVideo = isVideoItem(item);
    const thumbnailUri = isVideo ? resolveImageUri(item) : null;

    let mediaThumbnail: React.ReactNode;
    if (isVideo && thumbnailUri) {
      mediaThumbnail = (
        <RNImage
          source={{ uri: thumbnailUri }}
          style={{ width, height }}
          resizeMode='cover'
        />
      );
    } else if (isVideo) {
      // Video with no thumbnail: dark placeholder so the cell isn't blank.
      mediaThumbnail = (
        <Box
          width={width}
          height={height}
          backgroundColor='black'
          justifyContent='center'
          alignItems='center'
        />
      );
    }

    const localUrl =
      image?.url?.startsWith('file:') ||
      image?.url?.startsWith('data:') ||
      (image as LocalFile)?.path?.startsWith('file:') ||
      (image as LocalFile)?.path?.startsWith('data:');
    const displayImage =
      localUrl && (image as LocalFile)?.path
        ? { ...image, url: (image as LocalFile).path }
        : image;

    return (
      <Pressable
        key={item.id ?? `${index}`}
        testID='chat-image-grid-cell'
        accessibilityLabel={`${isVideo ? 'Video' : 'Image'} ${
          index + 1
        } of ${count}`}
        onPress={() => onPress?.(item, index)}
        onLongPress={onLongPress}
        style={({ pressed }) => [
          { width, height, overflow: 'hidden' },
          pressed && { opacity: 0.8 },
        ]}>
        {mediaThumbnail ?? (
          <CachedImage
            image={displayImage as never}
            imageClientId={item.clientId ?? undefined}
            width={width}
            height={height}
            resizeMode='cover'
            contentType={(item as Document).contentType}
            from='chat-image-grid'
          />
        )}
        {isVideo && <PlayCircle />}

        {showOverlay && count > 4 && (
          <Box
            style={StyleSheet.absoluteFillObject}
            justifyContent='center'
            alignItems='center'
            backgroundColor='grey06'>
            <Text variant='heading2' color='white'>
              {`+${count - 4}`}
            </Text>
          </Box>
        )}

        {status === 'compressing' && (
          <ActivityIndicatorLoading imageWidth={width} />
        )}

        {status === 'uploading' && (
          <Box
            position='absolute'
            bottom={theme.spacing.xxs}
            left={theme.spacing.xxs}
            right={theme.spacing.xxs}
            height={theme.spacing.xxs}
            backgroundColor='white'
            borderRadius='xs'
            overflow='hidden'>
            <Box
              backgroundColor='greenSecondary'
              width={`${Math.floor(progress * 100)}%`}
              height='100%'
            />
          </Box>
        )}

        {localUrl && (status === 'success' || blobId !== '') && (
          <Box
            position='absolute'
            bottom={theme.spacing.xxs}
            right={theme.spacing.xxs}
            backgroundColor='greenSecondary'
            padding='xxxs'
            borderRadius='xs'>
            <Icon name='Check' color='white' variant='xxxs' />
          </Box>
        )}

        {status === 'error' && (
          <Box
            position='absolute'
            bottom={theme.spacing.xxs}
            right={theme.spacing.xxs}
            backgroundColor='alertRed'
            padding='xxs'
            borderRadius='xs'>
            <TouchableOpacity onPress={() => retryUpload?.(item as LocalFile)}>
              <Icon name='RotateCw' color='white' variant='xxxs' />
            </TouchableOpacity>
          </Box>
        )}

        {localUrl && !status && <ActivityIndicatorLoading imageWidth={width} />}
      </Pressable>
    );
  };

  // ── SINGLE IMAGE ─────────────────────────────────────────────────────────
  if (count === 1) {
    const isFirstVideo = isVideoItem(list[0]);
    if (!singleDims) {
      const fallbackW = maxWidth;
      const fallbackH = Math.round(maxWidth * 0.75);
      // Videos without a thumbnail still need renderCell (dark placeholder + play icon).
      // Only images that are still loading/have no URI show the grey skeleton.
      if (isFirstVideo) {
        return (
          <Box
            width={fallbackW}
            height={fallbackH}
            borderRadius='s'
            overflow='hidden'>
            {renderCell(list[0], 0, fallbackW, fallbackH)}
          </Box>
        );
      }
      return (
        <Box
          width={fallbackW}
          height={fallbackH}
          backgroundColor='grey02'
          borderRadius='s'
        />
      );
    }
    return (
      <Box
        width={singleDims.w}
        height={singleDims.h}
        borderRadius='s'
        overflow='hidden'>
        {renderCell(list[0], 0, singleDims.w, singleDims.h)}
      </Box>
    );
  }

  // ── 2 IMAGES ─────────────────────────────────────────────────────────────
  if (count === 2) {
    if (isFirstLandscape) {
      return (
        <Box
          width={maxWidth}
          height={fullH * 2 + CHAT_IMAGE_GRID_GAP}
          borderRadius='s'
          overflow='hidden'>
          {renderCell(list[0], 0, maxWidth, fullH)}
          <Box height={CHAT_IMAGE_GRID_GAP} />
          {renderCell(list[1], 1, maxWidth, fullH)}
        </Box>
      );
    }
    return (
      <Box
        width={maxWidth}
        flexDirection='row'
        borderRadius='s'
        overflow='hidden'>
        {renderCell(list[0], 0, cellSize, cellH)}
        <Box width={CHAT_IMAGE_GRID_GAP} />
        {renderCell(list[1], 1, cellSize, cellH)}
      </Box>
    );
  }

  // ── 3 IMAGES ─────────────────────────────────────────────────────────────
  if (count === 3) {
    if (isFirstLandscape) {
      return (
        <Box
          width={maxWidth}
          height={fullH + CHAT_IMAGE_GRID_GAP + cellH}
          borderRadius='s'
          overflow='hidden'>
          {renderCell(list[0], 0, maxWidth, fullH)}
          <Box height={CHAT_IMAGE_GRID_GAP} />
          <Box flexDirection='row' height={cellH}>
            {renderCell(list[1], 1, cellSize, cellH)}
            <Box width={CHAT_IMAGE_GRID_GAP} />
            {renderCell(list[2], 2, cellSize, cellH)}
          </Box>
        </Box>
      );
    }
    const rightCellH = Math.round((cellH - CHAT_IMAGE_GRID_GAP) / 2);
    return (
      <Box
        width={maxWidth}
        height={cellH}
        flexDirection='row'
        borderRadius='s'
        overflow='hidden'>
        {renderCell(list[0], 0, cellSize, cellH)}
        <Box width={CHAT_IMAGE_GRID_GAP} />
        <Box width={cellSize} height={cellH}>
          {renderCell(list[1], 1, cellSize, rightCellH)}
          <Box height={CHAT_IMAGE_GRID_GAP} />
          {renderCell(list[2], 2, cellSize, rightCellH)}
        </Box>
      </Box>
    );
  }

  // ── 4+ IMAGES (2×2 grid, optional +N overlay on cell 4) ──────────────────
  const cells = list.slice(0, 4);
  return (
    <Box
      width={maxWidth}
      height={cellSize * 2 + CHAT_IMAGE_GRID_GAP}
      borderRadius='s'
      overflow='hidden'>
      <Box flexDirection='row' height={cellSize}>
        {renderCell(cells[0], 0, cellSize, cellSize)}
        <Box width={CHAT_IMAGE_GRID_GAP} />
        {renderCell(cells[1], 1, cellSize, cellSize)}
      </Box>
      <Box height={CHAT_IMAGE_GRID_GAP} />
      <Box flexDirection='row' height={cellSize}>
        {renderCell(cells[2], 2, cellSize, cellSize)}
        <Box width={CHAT_IMAGE_GRID_GAP} />
        {renderCell(cells[3], 3, cellSize, cellSize, count > 4)}
      </Box>
    </Box>
  );
};

// eslint-disable-next-line import/no-default-export
export default ChatImageGrid;
